import { Flex, Modal } from '@taxbit-private/cosmic'
import { RhfDropdown, RhfTextInput } from '@taxbit-private/cosmic-react-hook-form'
import React from 'react'
import { FormProvider } from 'react-hook-form'

import { OrgType } from 'components/admin-navbar/org-select/OrgMetadataSelect'
import { useCreateOrg } from 'hooks/data/orgsHook'
import useAppStore from 'store/useAppStore'
import { isZeusEnvironment } from 'utils/environment'

import useCreateOrgFormMethods, { CreateOrgFormValues } from './hooks/useCreateOrgFormMethods'

type CreateOrgModalProps = {
  isOpen: boolean
  onClose: () => void
}

enum TrackingId {
  Close = 'create-org-modal-close',
  Submit = 'create-org-modal-submit',
  Cancel = 'create-org-modal-cancel',
  Input = 'create-org-name-input',
  Type = 'create-org-type-input',
  SuccessToast = 'create-org-success-toast',
  ErrorToast = 'create-org-error-toast',
}

const ORG_TYPE_OPTIONS = [
  {
    label: 'Internal',
    value: OrgType.Internal,
  },
  {
    label: 'Customer',
    value: OrgType.Customer,
  },
  {
    label: 'Prospect',
    value: OrgType.Prospect,
  },
] as const

const CREATE_ORG_FORM_ID = 'create-org-form'

const CreateOrgModal: React.FC<CreateOrgModalProps> = ({ isOpen, onClose }) => {
  const formMethods = useCreateOrgFormMethods()
  const { mutate: createOrg, isLoading: isCreatingOrg } = useCreateOrg()
  const addCosmicToast = useAppStore((store) => store.addCosmicToast)

  return (
    <FormProvider {...formMethods}>
      <Flex
        as="form"
        id={CREATE_ORG_FORM_ID}
        onSubmit={(event: React.FormEvent) => {
          void formMethods.handleSubmit((values: CreateOrgFormValues) =>
            createOrg(values, {
              onSuccess: () => {
                addCosmicToast({
                  variant: 'primary',
                  message: 'Organization created successfully',
                  trackingId: TrackingId.SuccessToast,
                })
                onClose()
              },
              onError: () => {
                addCosmicToast({
                  variant: 'danger',
                  message: 'Failed to create organization',
                  trackingId: TrackingId.ErrorToast,
                })
              },
            })
          )(event)
        }}
      >
        <Modal
          isOpen={isOpen}
          title="Create Organization"
          closeButtonTrackingId={TrackingId.Close}
          onClose={onClose}
          primaryButtonProps={{
            label: 'Create',
            type: 'submit',
            form: CREATE_ORG_FORM_ID,
            trackingId: TrackingId.Submit,
            isDisabled: isCreatingOrg,
            isLoading: isCreatingOrg,
            loadingText: 'Creating...',
          }}
          secondaryButtonProps={{
            label: 'Cancel',
            onClick: onClose,
            trackingId: TrackingId.Cancel,
          }}
        >
          <Flex direction="column" gap="l">
            <RhfTextInput<CreateOrgFormValues>
              name="name"
              label="Name"
              required={true}
              trackingId={TrackingId.Input}
              placeholder="Enter organization name"
              maxLength={127}
            />
            {!isZeusEnvironment() && (
              <RhfDropdown<CreateOrgFormValues, 'metadata.type', (typeof ORG_TYPE_OPTIONS)[number]>
                name="metadata.type"
                label="Type"
                trackingId={TrackingId.Type}
                options={ORG_TYPE_OPTIONS}
                getOptionKey={({ value }) => value}
                getOptionLabel={({ label }) => label}
                getOptionValue={({ value }) => value}
                shouldUsePortal={true}
              />
            )}
          </Flex>
        </Modal>
      </Flex>
    </FormProvider>
  )
}

export default CreateOrgModal
